/* @flow */
/* eslint-env es6,browser */

/**
 * @description module for promise related utility functions
 * @module promiseutils
 * @requires module:coreutils
 */
import { isNumber } from './coreutils';

/**
 * Generates promise which rejects after timeout
 *
 * @param {number} ms - timeout in millisecs
 * @param {string} cxt - context to be passed in reject message
 * @returns {Promise} - Promise rejects with timeout
 */
export function getTimeoutPromise(ms: number, cxt: string = ''): Promise<void> {
  if (!isNumber(ms) || ms <= 0) {
    return Promise.reject();
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (cxt.length) {
        cxt += ':';
      }
      reject(`${cxt}timeout`);
    }, ms);
  });
}

/**
 * RSVP equivalent of allSettled
 *
 * @global
 * @typedef {object} allSettledPerPromiseResp
 * @property {string} state=(fullfilled|rejected)
 * @property {*} value - data returned in promise resolve
 * @property {*} reason - error reason (data passed to promise reject)
 * @param {Array<Promise<*>>} arr - Array of promise
 * @returns {Promise<Array<allSettledPerPromiseResp>>} - Promise returns with fulfilled/rejectd status
 */
export function allSettledPromise(
  arr: Array<Promise<any>> // flowlint-line unclear-type:off
): Promise<arrSettledPromise> {
  arr = arr || [];
  return Promise.all(
    arr.map(p =>
      p.then(
        v => ({ state: 'fulfilled', reason: '', value: v }),
        e => ({ state: 'rejected', reason: e, value: '' })
      )
    )
  );
}

/**
 * RSVP equivalent of allSettled with timeout
 *
 * @global
 * @typedef {object} arrSettledTimedPromise
 * @property {string} state=(fullfilled|rejected)
 * @property {*} value - data returned in promise resolve
 * @property {*} reason - error reason (data passed to promise reject)
 * @property {string} name - name passed as input for each promise
 * @property {number} tt - time taken
 * @param {Array<Promise<*>>} arr - Array of promise
 * @returns {Promise<arrSettledTimedPromise>} - Promise returns with fulfilled/rejectd status
 */
export function allSettledTimedPromise(
  arr: Array<{ name: string, promise: Promise<any> }> // flowlint-line unclear-type:off
): Promise<arrSettledTimedPromise> {
  arr = arr || [];
  const startTime = Date.now();
  return Promise.all(
    arr.map(o =>
      o.promise.then(
        v => ({
          name: o.name,
          state: 'fulfilled',
          reason: '',
          value: v,
          tt: Date.now() - startTime
        }),
        e => ({
          name: o.name,
          state: 'rejected',
          reason: e,
          value: '',
          tt: Date.now() - startTime
        })
      )
    )
  );
}
